import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PublishIcon from "@mui/icons-material/Publish";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GridApi } from "ag-grid-community";
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import { useEffect, useState } from "react";

import {
	ChemistryElement,
	ChemistryRangeRowDraft,
	ChemistryRangeVersion,
	ChemistryRangesPayload,
} from "../../models/ChemistryRange";
import {
	confirmChemistryRangeVersion,
	deleteChemistryRangeVersion,
	getChemistryRangeVersions,
	getChemistryRangesByGrade,
	unscheduleChemistryRangeVersion,
	upsertChemistryRanges,
} from "../../repositories/ChemistryRangeRepository";
import { ConfirmVersionModal } from "../../versions/ConfirmVersionModal";
import { StatusIcon, VERSION_STATUS } from "../../versions/versionStatus";
import { ChemistryRangesTable } from "./components/ChemistryRangesTable";
import { ColumnsMenu } from "./components/ColumnsMenu";

const versionLabel = (version: ChemistryRangeVersion) =>
	version.StatusCode === VERSION_STATUS.DRAFT
		? "Draft"
		: `Rev ${version.Rev} - ${new Date(
				version.EffectiveDate ?? new Date(),
			).toLocaleDateString()}`;

const currentVersionId = (versions: ChemistryRangeVersion[]) =>
	versions
		.filter((v) => v.StatusCode === VERSION_STATUS.PUBLISHED)
		.sort(
			(a, b) =>
				new Date(b.EffectiveDate ?? new Date()).getTime() -
				new Date(a.EffectiveDate ?? new Date()).getTime(),
		)[0]?.ChemistryRangeVersionId ?? null;

const VersionOption = ({
	version,
	isCurrent,
}: {
	version: ChemistryRangeVersion;
	isCurrent: boolean;
}) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
		<StatusIcon
			status={version.StatusCode}
			isCurrent={isCurrent}
			date={
				version.EffectiveDate == null
					? undefined
					: new Date(version.EffectiveDate).toLocaleDateString()
			}
		/>
		{versionLabel(version)}
	</Box>
);

const buildDrafts = (
	payload: ChemistryRangesPayload | undefined,
): ChemistryRangeRowDraft[] => {
	if (!payload) return [];

	return payload.rows.map((row) => {
		const limitsOfRow = payload.limits.filter(
			(limit) =>
				limit.ChemistryRangesByGradeId === row.ChemistryRangesByGradeId,
		);

		return {
			RowKey: `saved-${row.ChemistryRangesByGradeId}`,
			SWV: row.SWV,
			GradeDescription: row.GradeDescription,
			Customer: row.Customer,
			COP: row.COP,
			Additions: row.Additions,
			Limits: Object.fromEntries(
				limitsOfRow
					.filter((limit) => limit.SectionId == null)
					.map((limit) => [
						limit.ElementName,
						{ MinValue: limit.MinValue, MaxValue: limit.MaxValue },
					]),
			),
			SectionLimits: limitsOfRow
				.filter((limit) => limit.SectionId != null)
				.map((limit) => ({
					Element: limit.ElementName,
					SectionId: limit.SectionId as number,
					SectionName: limit.SectionName,
					MinValue: limit.MinValue,
					MaxValue: limit.MaxValue,
				})),
		};
	});
};

export const useChemistryRangeVersions = () =>
	useQuery<ChemistryRangeVersion[], Error>({
		queryKey: ["chemistryRangeVersions"],
		queryFn: ({ signal }) => getChemistryRangeVersions(signal),
	});

export const useChemistryRanges = (versionId: number | null) =>
	useQuery<ChemistryRangesPayload, Error>({
		queryKey: ["chemistryRangesByGrade", versionId],
		queryFn: ({ signal }) => getChemistryRangesByGrade({ versionId }, signal),
		enabled: versionId != null,
	});

export const ChemistryRangesPage = ({
	readOnly = false,
}: {
	readOnly?: boolean;
}) => {
	const queryClient = useQueryClient();

	const [error, setError] = useState("");
	const [filterValue, setFilterValue] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [selectedVersionId, setSelectedVersionId] = useState<number | null>(
		null,
	);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editVersionId, setEditVersionId] = useState<number | null>(null);
	const [editComments, setEditComments] = useState<string>("");
	const [editRows, setEditRows] = useState<ChemistryRangeRowDraft[]>([]);
	const [visibleColumns, setVisibleColumns] = useQueryState(
		"cols",
		parseAsArrayOf(parseAsString),
	);
	const [confirmVersionOpen, setConfirmVersionOpen] = useState(false);
	const [confirm, setConfirm] = useState<"changeToDraft" | "delete" | null>(
		null,
	);

	const {
		data: allVersions,
		isLoading: versionsLoading,
		error: versionsError,
		isError: isVersionsError,
	} = useChemistryRangeVersions();

	const versions = readOnly
		? allVersions?.filter((v) => v.StatusCode !== VERSION_STATUS.DRAFT)
		: allVersions;
	const orderedVersions = [...(versions ?? [])].sort(
		(a, b) =>
			Number(b.StatusCode === VERSION_STATUS.DRAFT) -
			Number(a.StatusCode === VERSION_STATUS.DRAFT),
	);

	const defaultVersionId =
		versions?.find((v) => v.StatusCode === VERSION_STATUS.PUBLISHED)
			?.ChemistryRangeVersionId ??
		versions?.[0]?.ChemistryRangeVersionId ??
		null;

	const versionId = selectedVersionId ?? defaultVersionId;
	const currentId = currentVersionId(versions ?? []);
	const selectedVersion =
		versions?.find((v) => v.ChemistryRangeVersionId === versionId) ?? null;

	const isDraft =
		!readOnly && selectedVersion?.StatusCode === VERSION_STATUS.DRAFT;
	const isScheduled =
		!readOnly && selectedVersion?.StatusCode === VERSION_STATUS.SCHEDULED;
	const hasDraft = (allVersions ?? []).some(
		(v) => v.StatusCode === VERSION_STATUS.DRAFT,
	);
	const hasPendingVersion = (allVersions ?? []).some(
		(v) => v.StatusCode !== VERSION_STATUS.PUBLISHED,
	);
	const confirmedVersions = (allVersions ?? []).filter((v) => v.IsConfirmed);
	const maxRev = confirmedVersions.reduce(
		(max, v) => Math.max(max, v.Rev ?? 0),
		0,
	);
	const lastEffectiveDate = confirmedVersions.reduce<Date | null>(
		(last, v) =>
			v.EffectiveDate != null &&
			(last == null || new Date(v.EffectiveDate) > new Date(last))
				? v.EffectiveDate
				: last,
		null,
	);

	const {
		data: payload,
		isLoading: rowsLoading,
		error: rowsError,
		isError: isRowsError,
	} = useChemistryRanges(versionId);

	const invalidateVersions = async () => {
		await queryClient.invalidateQueries({
			queryKey: ["chemistryRangeVersions"],
		});
		await queryClient.invalidateQueries({
			queryKey: ["chemistryRangesByGrade"],
		});
	};

	const saveMutation = useMutation({
		mutationFn: upsertChemistryRanges,
		meta: { successMessage: "Draft saved" },
		onSuccess: async (savedId: number) => {
			await invalidateVersions();
			setSelectedVersionId(savedId);
			setIsEditMode(false);
			setEditRows([]);
		},
		onError: (e: Error) => setError(e.message),
	});

	const confirmMutation = useMutation({
		mutationFn: confirmChemistryRangeVersion,
		meta: { successMessage: "Specification confirmed" },
		onSuccess: async () => {
			await invalidateVersions();
			setConfirmVersionOpen(false);
		},
		onError: (e: Error) => setError(e.message),
	});

	const unscheduleMutation = useMutation({
		mutationFn: unscheduleChemistryRangeVersion,
		meta: { successMessage: "Scheduled revision changed to Draft" },
		onSuccess: async () => {
			await invalidateVersions();
		},
		onError: (e: Error) => setError(e.message),
	});

	const deleteMutation = useMutation({
		mutationFn: deleteChemistryRangeVersion,
		meta: { successMessage: "Draft deleted" },
		onSuccess: async () => {
			setSelectedVersionId(null);
			await invalidateVersions();
		},
		onError: async (e: Error) => {
			setError(e.message);
			await invalidateVersions();
		},
	});

	const savedRows = buildDrafts(payload);
	const displayedRows = isEditMode ? editRows : savedRows;
	const elements = payload?.elements ?? [];
	const byName = (a: ChemistryElement, b: ChemistryElement) =>
		a.ElementName.localeCompare(b.ElementName);

	const usedNames = new Set<string>();
	for (const row of savedRows) {
		for (const [element, limit] of Object.entries(row.Limits)) {
			if (limit.MinValue !== null || limit.MaxValue !== null) {
				usedNames.add(element);
			}
		}
	}
	const defaultVisible = elements
		.filter((element) => usedNames.has(element.ElementName))
		.map((element) => element.ElementName);
	const visible = visibleColumns ?? defaultVisible;

	const shown = elements.filter((element) =>
		visible.includes(element.ElementName),
	);
	const measuredColumns = [...shown].sort(byName);

	const toggleColumn = (elementName: string) => {
		const next = visible.includes(elementName)
			? visible.filter((name) => name !== elementName)
			: [...visible, elementName];
		const isDefault =
			next.length === defaultVisible.length &&
			next.every((name) => defaultVisible.includes(name));
		setVisibleColumns(isDefault ? null : next);
	};

	const startEditing = (draftId: number | null) => {
		setEditVersionId(draftId);
		setEditComments(selectedVersion?.Comments ?? "");
		setEditRows(
			savedRows.map((row) => ({
				...row,
				Limits: Object.fromEntries(
					Object.entries(row.Limits).map(([element, limit]) => [
						element,
						{ ...limit },
					]),
				),
				SectionLimits: row.SectionLimits.map((limit) => ({ ...limit })),
			})),
		);
		setIsEditMode(true);
	};

	const handleCancel = () => {
		setIsEditMode(false);
		setEditRows([]);
		setEditVersionId(null);
		setEditComments("");
	};

	const handleSave = () => {
		for (const row of editRows) {
			if (
				!row.SWV ||
				row.SWV < 1 ||
				row.SWV > 999 ||
				!Number.isInteger(row.SWV)
			) {
				setError("Every row needs a whole SWV between 1 and 999.");
				return;
			}
		}

		const swvSet = new Set<number>();
		for (const row of editRows) {
			if (swvSet.has(row.SWV ?? 0)) {
				setError("SWV values must be unique.");
				return;
			}
			swvSet.add(row.SWV ?? 0);
		}

		for (const row of editRows) {
			for (const [element, limit] of Object.entries(row.Limits)) {
				if (
					limit.MinValue != null &&
					limit.MaxValue != null &&
					limit.MinValue > limit.MaxValue
				) {
					setError(
						`SWV ${row.SWV}: the ${element} minimum is above its maximum.`,
					);
					return;
				}
			}
		}

		saveMutation.mutate({
			versionId: editVersionId,
			comments: editComments || null,
			rows: editRows,
		});
	};

	const handleConfirmClose = (value?: string) => {
		const action = confirm;
		setConfirm(null);
		if (value !== "OK" || versionId == null) return;
		if (action === "changeToDraft") unscheduleMutation.mutate({ versionId });
		if (action === "delete") deleteMutation.mutate({ versionId });
	};

	useEffect(() => {
		if (isVersionsError && versionsError) {
			setError(versionsError.message);
		}
		if (isRowsError && rowsError) {
			setError(rowsError.message);
		}
	}, [isVersionsError, versionsError, isRowsError, rowsError]);

	const isBusy =
		saveMutation.isPending ||
		confirmMutation.isPending ||
		unscheduleMutation.isPending ||
		deleteMutation.isPending;

	return (
		<>
			{!readOnly && <HelmetDexteel title="Chemistry Ranges" />}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: readOnly ? "78vh" : "85vh",
					p: 1,
					gap: 2,
				}}
			>
				{!readOnly && (
					<Typography variant="h5" fontWeight={600}>
						Chemistry Ranges
					</Typography>
				)}

				<Box
					sx={{
						display: "flex",
						gap: 2,
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
					}}
				>
					<Box
						sx={{
							display: "flex",
							gap: 2,
							alignItems: "flex-start",
							flexWrap: "wrap",
						}}
					>
						{!readOnly && (
							<FormControl size="small" sx={{ minWidth: 240 }}>
								<InputLabel id="chemistry-version-label">Version</InputLabel>
								<Select
									labelId="chemistry-version-label"
									label="Version"
									data-test-id="version-selector"
									value={versionId ?? ""}
									disabled={versionsLoading || isEditMode}
									onChange={(e) => setSelectedVersionId(Number(e.target.value))}
									sx={{
										backgroundColor: (theme) => theme.palette.background.paper,
									}}
									renderValue={(value) => {
										const version = versions?.find(
											(v) => v.ChemistryRangeVersionId === value,
										);
										return version ? (
											<VersionOption
												version={version}
												isCurrent={
													version.ChemistryRangeVersionId === currentId
												}
											/>
										) : null;
									}}
								>
									{orderedVersions?.map((v) => (
										<MenuItem
											key={v.ChemistryRangeVersionId}
											value={v.ChemistryRangeVersionId}
										>
											<VersionOption
												version={v}
												isCurrent={v.ChemistryRangeVersionId === currentId}
											/>
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}

						<Box sx={{ minWidth: 280, mt: -1 }}>
							<TextFieldSearchFilter
								value={filterValue}
								onChange={(val) => {
									setFilterValue(val);
									gridApi?.setGridOption("quickFilterText", val);
								}}
							/>
						</Box>

						<ColumnsMenu
							elements={elements}
							visible={visible}
							used={usedNames}
							onToggle={toggleColumn}
						/>

						{isDraft && (
							<Chip
								label="Draft"
								color="warning"
								size="small"
								data-test-id="draft-chip"
							/>
						)}
						{isScheduled && (
							<Chip
								label="Scheduled"
								color="info"
								size="small"
								data-test-id="scheduled-chip"
							/>
						)}
					</Box>

					{!readOnly && (
						<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
							{isEditMode ? (
								<>
									<Button
										variant="outlined"
										size="small"
										color="error"
										startIcon={<CancelIcon />}
										onClick={handleCancel}
										data-test-id="cancel-edit-button"
										disabled={saveMutation.isPending}
										sx={{
											backgroundColor: (theme) =>
												theme.palette.background.paper,
										}}
									>
										Cancel
									</Button>
									<Button
										variant="contained"
										size="small"
										startIcon={
											saveMutation.isPending ? (
												<CircularProgress size={16} color="inherit" />
											) : (
												<SaveIcon />
											)
										}
										onClick={handleSave}
										data-test-id="save-draft-button"
										disabled={saveMutation.isPending}
									>
										Save
									</Button>
								</>
							) : isDraft ? (
								<>
									<Button
										variant="outlined"
										color="error"
										size="small"
										startIcon={<DeleteOutlineIcon />}
										onClick={() => setConfirm("delete")}
										data-test-id="delete-draft-button"
										disabled={isBusy || rowsLoading}
										sx={{
											backgroundColor: (theme) =>
												theme.palette.background.paper,
										}}
									>
										Delete Draft
									</Button>
									<Button
										variant="outlined"
										size="small"
										startIcon={<EditOutlinedIcon />}
										onClick={() => startEditing(versionId)}
										data-test-id="edit-draft-button"
										disabled={isBusy || rowsLoading}
										sx={{
											backgroundColor: (theme) =>
												theme.palette.background.paper,
										}}
									>
										Edit Draft
									</Button>
									<Button
										variant="contained"
										color="success"
										size="small"
										startIcon={<PublishIcon />}
										onClick={() => setConfirmVersionOpen(true)}
										data-test-id="confirm-draft-button"
										disabled={isBusy || rowsLoading}
									>
										Confirm
									</Button>
								</>
							) : isScheduled ? (
								<Button
									variant="outlined"
									size="small"
									startIcon={<UndoIcon />}
									onClick={() => setConfirm("changeToDraft")}
									data-test-id="change-to-draft-button"
									disabled={isBusy || hasDraft}
								>
									Change to Draft
								</Button>
							) : (
								<Tooltip
									title={
										hasPendingVersion
											? "There is already a draft or scheduled version"
											: ""
									}
								>
									<span data-test-id="new-version-button-wrapper">
										<Button
											variant="contained"
											size="small"
											startIcon={<AddIcon />}
											onClick={() => startEditing(null)}
											data-test-id="new-version-button"
											disabled={
												isBusy ||
												rowsLoading ||
												versionsLoading ||
												hasPendingVersion
											}
										>
											New Version
										</Button>
									</span>
								</Tooltip>
							)}
						</Box>
					)}
				</Box>

				<Box sx={{ flex: 1, minHeight: 0 }}>
					<ChemistryRangesTable
						rows={displayedRows}
						elements={measuredColumns}
						isLoading={versionsLoading || rowsLoading}
						isEditMode={isEditMode}
						onRowsChange={() => setEditRows((prev) => [...prev])}
						onGridReady={setGridApi}
					/>
				</Box>

				{isEditMode ? (
					<TextField
						label="Comments / Footnotes"
						slotProps={{
							htmlInput: { "data-test-id": "draft-comments-input" },
						}}
						multiline
						minRows={2}
						maxRows={4}
						size="small"
						fullWidth
						value={editComments}
						onChange={(e) => setEditComments(e.target.value)}
					/>
				) : (
					selectedVersion?.Comments && (
						<Typography
							variant="body2"
							color="text.secondary"
							data-test-id="version-comments"
							sx={{ whiteSpace: "pre-line" }}
						>
							{selectedVersion.Comments}
						</Typography>
					)
				)}
			</Box>

			<ConfirmVersionModal
				open={confirmVersionOpen}
				maxRev={maxRev}
				lastEffectiveDate={lastEffectiveDate}
				allowPastDates
				isSubmitting={confirmMutation.isPending}
				onClose={() => setConfirmVersionOpen(false)}
				onSubmit={({ rev, effectiveDate }) => {
					if (versionId == null) return;
					confirmMutation.mutate({ versionId, rev, effectiveDate });
				}}
			/>

			<ConfirmationDialogRaw
				open={confirm !== null}
				title={
					confirm === "changeToDraft"
						? "Change Scheduled Revision to Draft"
						: "Delete Draft"
				}
				message={
					confirm === "changeToDraft"
						? "Change scheduled revision to Draft? Its saved ranges will be preserved. It will no longer become effective automatically."
						: "Delete and discard this draft? Confirmed revisions are unaffected."
				}
				buttons={{
					no: "Cancel",
					yes: confirm === "changeToDraft" ? "Change to Draft" : "Delete Draft",
				}}
				onClose={handleConfirmClose}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
